import { computed, Injectable, Signal, signal, WritableSignal } from "@angular/core";
import { form } from "@angular/forms/signals";
import {
    defaultBuildStatusOptions,
    defaultNewDwellingTypes,
    defaultNumberOfMonthsToShow,
    defaultNumberOfUnits,
    defaultOwnershipOptions,
    defaultSaleStatusOptions,
    NewDwellingFilterModel,
} from "src/app/ev-map/new-dwellings-map/new-dwellings-filter/new-dwellings-filter.model";
import { defaultListSortKey } from "src/app/ev-map/new-dwellings-map/new-dwelling-listings/new-dwellings-list/new-dwellings-list-sort.model";
import { INewDwellingsFilter } from "src/models/new-dwelling/new-dwelling.model";
import { CompareUtil } from "src/util/tools/compare.util";
@Injectable()
export class NewDwellingSalesStateService {
    private createDefaultFilterModel(): NewDwellingFilterModel {
        return {
            dwellingTypes: defaultNewDwellingTypes.map((t) => ({ ...t })),
            ownershipOptions: defaultOwnershipOptions.map((o) => ({ ...o })),
            buildStatusOptions: defaultBuildStatusOptions.map((o) => ({ ...o })),
            saleStatusOptions: defaultSaleStatusOptions.map((o) => ({ ...o })),
            numberOfUnits: { ...defaultNumberOfUnits },
            timePeriod: {
                numberOfMonthsToShow: defaultNumberOfMonthsToShow,
                useFromToDate: false,
                fromDate: null,
                toDate: null,
                isAllSelected: false,
            },
        };
    }

    filterModel: WritableSignal<NewDwellingFilterModel> = signal(this.createDefaultFilterModel());
    defaultFilterModel: Signal<NewDwellingFilterModel> = computed(() => this.createDefaultFilterModel());

    filterForm = form(this.filterModel);

    // API-ready filter value — recomputes (and triggers a refetch) on every change.
    // Must match INewDwellingsFilter exactly; the data service depends on this shape.
    filterFormAPI: Signal<INewDwellingsFilter> = computed(() => this.getFilterFormAPI());

    getFilterFormAPI(): INewDwellingsFilter {
        const filter = {
            estateTypes: this.filterForm
            .dwellingTypes()
            .value()
            .filter((t) => t.selected)
            .map((t) => t.estateType),
            ownershipTypes: this.filterForm
            .ownershipOptions()
            .value()
            .filter((o) => o.selected)
            .map((o) => o.value),
            buildStatus: this.filterForm
            .buildStatusOptions()
            .value()
            .filter((o) => o.selected)
            .map((o) => o.value),
            saleStatus: this.filterForm
            .saleStatusOptions()
            .value()
            .filter((o) => o.selected)
            .map((o) => o.value),
            numberOfUnits: this.filterForm.numberOfUnits().value(),
            numberOfMonthsToShow: this.filterForm.timePeriod.numberOfMonthsToShow().value(),
            useFromToDate: this.filterForm.timePeriod.useFromToDate().value(),
            fromDate: this.filterForm.timePeriod.fromDate().value() ?? undefined,
            toDate: this.filterForm.timePeriod.toDate().value() ?? undefined,
            isAllSelected: this.filterForm.timePeriod.isAllSelected().value(),
        };
        return filter;
    }


    private readonly _listSortKey: WritableSignal<string> = signal(defaultListSortKey);
    readonly listSortKey: Signal<string> = this._listSortKey.asReadonly();

    setListSortKey(key: string): void {
        this._listSortKey.set(key || defaultListSortKey);
    }

    isDirty = computed(() => !CompareUtil.deepEquals(this.filterForm().value(), this.defaultFilterModel()));

    resetFilter(): void {
        this.filterForm().reset();
        this.filterModel.set(this.createDefaultFilterModel());
    }
}
